import { useAuth0 } from '@auth0/auth0-react';
import { AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './App.scss';
import Loader from './components/common/loader';
import sessionStorage from './helpers/sessionStorage';
import storage from './helpers/storage';
import { isExpiredTime, isTokenAboutToExpire } from './helpers/utils';
import RedirectLogin from './redirectLogin';
import { useCustomStore } from './redux/store';
import AppRoutes from './routes';
import { auth0LoginSignUpAPI, refreshTokenAPI } from './services/auth.service';
import { redirectionRoutes } from './routes/redirectionRoutes';

function App() {
    const { isAuthenticated, isLoading, getIdTokenClaims } = useAuth0();
    const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
    const { auth0ButtonClicked, manualRefreshToken, appLoader, setUser, setAuth0Login, setAuth0ButtonClicked, setAppLoader, setManualRefreshToken } = useCustomStore();
    const navigate = useNavigate();
    
    const getRefreshToken = () => {
        const token = storage.getRefreshToken() as string;
        const formData: { refreshToken: string } = {
            refreshToken: token
        };
        setAppLoader(true);
        refreshTokenAPI(formData).then((response: any) => {
            setAppLoader(false);
            updateTokenHelper(response);
        }).catch((error: AxiosResponse) => {
            setAppLoader(false);
        });
    };

    const updateTokenHelper = (response: any) => {
        storage.setToken(response.data.token.access.token);
        storage.setRefreshToken(response.data.token.refresh.token);
        setUser(response.data?.user);
    }

    useEffect(() => {
        /**
         * On reload decode and store token in redux state
         */
        const token: string | null = storage.getToken();
        if (token !== null) {
            getRefreshToken();
        }

        /**
         * On reload check if token is about to expire
         * Refresh the token.
         */
        const tokenExpireInTwoMinutes: boolean = isTokenAboutToExpire();
        if (token !== null && tokenExpireInTwoMinutes) {
            getRefreshToken();
        }
    }, []);

     /**
     * when a manual refresh token is triggered.
     * It calls the refresh token api.
     */
    useEffect(() => {
        if (manualRefreshToken) {
            getRefreshToken();
            setManualRefreshToken(false);
        }
    }, [manualRefreshToken]);
    

    /**
     * When auth0 used as login/signup
     */
    const auth0Helper = (formData: any) => {
        setIsAuthenticating(true)
        auth0LoginSignUpAPI(formData).then((response: any) => {
            setIsAuthenticating(false)
            if(response.data) {
                if (response.data.isUsing2FA) {
                    toast(response.message, { type: toast.TYPE.INFO });
                    sessionStorage.setData("2fa", response.data.email);
                    navigate(redirectionRoutes.twoFactorAuthentication.path, { state: { email: response.data.email, auth0Login: true }, replace: true });
                } else {
                    toast(response.message, { type: toast.TYPE.SUCCESS });
                    setAuth0Login(true);
                    updateTokenHelper(response);
                    if(!response.data?.user?.profileVerification?.status) {
                        navigate(redirectionRoutes.profile.path, { replace: true });
                    } else {
                        navigate(redirectionRoutes.dashboard.path, { replace: true });
                    }
                }
            } else {
                toast(response.message, { type: toast.TYPE.SUCCESS });
            }
        }, (err: any) => {
            console.error(err)
            toast(err.data.message, { type: toast.TYPE.ERROR });
            // navigate('/two-factor-authentication', { state: { email: values.email }, replace: true });
            setIsAuthenticating(false)
        })
    }

    useEffect(() => {
        if(isAuthenticated && auth0ButtonClicked && !isAuthenticating) {
            setAuth0ButtonClicked(false);
            setIsAuthenticating(true)
            getIdTokenClaims().then((user: any) => {
                if(!isExpiredTime(user.exp)) {
                    const formData = {
                        fullName: user.nickname,
                        email: user.email,
                        auth0: {
                            sid: user.sid,
                            emailVerified: user.email_verified
                        }
                    };
                    auth0Helper(formData)
                } else {
                    console.log("auth0 expired")
                }
            }, (err) => {
                console.error(1, err)
            })
        }
    }, [isAuthenticated])

    
    if((isLoading || isAuthenticating) && !storage.getToken()) {
        return <Loader isLoading={true} />
    }

    return (
        <React.Suspense fallback={
            <Loader isLoading={true} />
        }>
            {appLoader ? <Loader isLoading={true} /> : ''}
            <RedirectLogin />
            <AppRoutes />
        </React.Suspense>
    );
}

export default App;
